// src/components/chat/FloatingChatWidget.tsx
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SendHorizontal } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageProvider";

/** Brand tokens — Kirusa + ConvoBuddy only */
const COLORS = {
  text: "#414141",
  muted: "#6E6E6E",
  border: "#E5E5E5",
  panelBg: "#FFFFFF",
  panelBgSoft: "#F9F9F9",
  teal: "#00B7B0",
  indigo: "#2F2A7A",
  indigoSoft: "#EBE8FF",
  red: "#D81F26",
};

type ChatMsg = {
  id: string;
  role: "user" | "agent" | "system" | "bot";
  text: string;
  ts: number;
};

type ChatError = "offline" | "unreachable" | null;

function useRoomId(): string {
  return useMemo(() => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return `room_${crypto.randomUUID()}`;
    }
    return `room_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }, []);
}

function makeClientId(prefix: string): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function dedupeMessages(items: ChatMsg[]): ChatMsg[] {
  const seen = new Set<string>();
  return (Array.isArray(items) ? items : []).filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function useDisplayName(): string {
  return useMemo(() => {
    const existing = localStorage.getItem("cb_chat_name");
    if (existing) return existing;
    const name = "Guest";
    localStorage.setItem("cb_chat_name", name);
    return name;
  }, []);
}

const DEV_PORT_FALLBACK = Number(import.meta.env.VITE_CHAT_WS_DEV_PORT ?? 8787);

function resolveWsBase(): string {
  const envUrlRaw = import.meta.env.VITE_CHAT_WS_URL?.toString().trim();
  if (envUrlRaw) {
    const envIsLocal = /localhost|127\.0\.0\.1/i.test(envUrlRaw);
    if (typeof window !== "undefined") {
      const hostIsLocal =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      if (!hostIsLocal && envIsLocal) {
        // Ignore localhost overrides when running on a public domain.
      } else {
        return envUrlRaw;
      }
    } else {
      return envUrlRaw;
    }
  }

  if (typeof window === "undefined")
    return `ws://localhost:${DEV_PORT_FALLBACK}`;

  const { protocol, hostname, port } = window.location;
  const scheme = protocol === "https:" ? "wss" : "ws";

  if (port && ["5173", "4173", "3000", "5174"].includes(port)) {
    return `${scheme}://${hostname}:${DEV_PORT_FALLBACK}`;
  }
  if (port) return `${scheme}://${hostname}:${port}`;
  return `${scheme}://${hostname}`;
}

function buildWsUrl(params: Record<string, string>) {
  const base = resolveWsBase();
  const qs = new URLSearchParams(params).toString();
  return `${base.replace(/\/$/, "")}/ws?${qs}`;
}

async function probeChatServer(base: string): Promise<boolean> {
  try {
    const httpBase = base.startsWith("wss")
      ? base.replace(/^wss/i, "https")
      : base.replace(/^ws/i, "http");
    const healthUrl = `${httpBase.replace(/\/$/, "")}/ws/health`;
    const res = await fetch(healthUrl, {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
    });
    return res.ok || res.type === "opaque";
  } catch {
    return false;
  }
}

export function FloatingChatWidget() {
  const location = useLocation();
  const roomId = useRoomId();
  const displayName = useDisplayName();

  const { t } = useTranslation("chat"); // 👉 new namespace
  const { lang } = useLanguage(); // "en" | "hi" | "fr" | "es"

  const [open, setOpen] = useState(false);
  const [connected, setConnected] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState<ChatError>(null);
  const [shouldConnect, setShouldConnect] = useState(false);
  const [launcherBottom, setLauncherBottom] = useState(32);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openRef = useRef(open);
  const sessionIdRef = useRef(makeClientId("session"));
  const launcherDragRef = useRef({
    active: false,
    startY: 0,
    startBottom: 32,
    moved: false,
  });

  // Hide on dashboard routes
  const onDashboard = location.pathname.startsWith("/dashboard");

  // Always keep scroll pinned to the bottom when we want it
  const scrollToBottom = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !shouldConnect) return;

    let cancelled = false;
    const base = resolveWsBase();

    const scheduleReconnect = (attempt: number) => {
      if (cancelled) return;
      const delay = Math.min(5000, 1000 * Math.pow(2, attempt));
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectTimer.current = setTimeout(
        () => connect(attempt + 1),
        delay
      );
    };

    const connect = async (attempt = 0) => {
      if (cancelled) return;

      const available = await probeChatServer(base);
      if (cancelled) return;

      if (!available) {
        setConnected(false);
        setError("offline");
        return;
      }

      const wsUrl = buildWsUrl({
        room: roomId,
        session: sessionIdRef.current,
        name: displayName,
        role: "user",
        lang, // 🌍 send language to backend
      });
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setConnected(true);
        setError(null);
      };

      ws.onmessage = (evt) => {
        if (cancelled) return;
        try {
          const payload = JSON.parse(String(evt.data));
          if (payload.type === "history" && Array.isArray(payload.items)) {
            setMessages(dedupeMessages(payload.items as ChatMsg[]).slice(-80));
            if (openRef.current) setUnread(0);
          } else if (payload.type === "message" && payload.msg) {
            const msg = payload.msg as ChatMsg;
            let added = false;
            setMessages((prev) => {
              if (!msg.id || prev.some((item) => item.id === msg.id)) {
                return prev;
              }
              added = true;
              return [...prev, msg].slice(-80);
            });
            if (!added) return;
            if (!openRef.current && msg.role !== "user") {
              setUnread((u) => Math.min(99, u + 1));
            }
          } else if (payload.type === "system" && payload.text) {
            setMessages((prev) => [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: "system",
                text: String(payload.text),
                ts: Date.now(),
              },
            ]);
            if (!openRef.current) setUnread((u) => Math.min(99, u + 1));
          }
        } catch {
          /* ignore malformed frames */
        }
      };

      ws.onerror = () => {
        if (cancelled) return;
        setError("unreachable");
      };

      ws.onclose = () => {
        if (cancelled) return;
        setConnected(false);
        // basic retry
        if (attempt < 5) scheduleReconnect(attempt);
        else setError("offline");
      };
    };

    connect(0);

    return () => {
      cancelled = true;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [roomId, displayName, shouldConnect, lang]);

  // Track "open" in a ref so ws handlers know if panel is visible
  useEffect(() => {
    openRef.current = open;
    if (open) setShouldConnect(true);
  }, [open]);

  useEffect(() => {
    if (!onDashboard) return;
    setOpen(false);
    setUnread(0);
  }, [onDashboard]);

  // When panel is opened, jump to bottom once
  useEffect(() => {
    if (open) {
      setTimeout(scrollToBottom, 0);
      setUnread(0);
    }
  }, [open, scrollToBottom]);

  // When new messages arrive WHILE panel is open, keep it scrolled to bottom
  useEffect(() => {
    if (!openRef.current) return;
    scrollToBottom();
  }, [messages.length, scrollToBottom]);

  const sendMessage = useCallback((text: string) => {
    const content = text.trim();
    if (!content) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "message", id: makeClientId("msg"), text: content }));
  }, []);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const body = input.trim();
    if (!body) return;
    sendMessage(body);
    setInput("");
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const clampLauncherBottom = (value: number) => {
    if (typeof window === "undefined") return value;
    const max = Math.max(32, window.innerHeight - 96);
    return Math.min(Math.max(value, 24), max);
  };

  const handleLauncherPointerDown: React.PointerEventHandler<HTMLButtonElement> = (event) => {
    launcherDragRef.current = {
      active: true,
      startY: event.clientY,
      startBottom: launcherBottom,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleLauncherPointerMove: React.PointerEventHandler<HTMLButtonElement> = (event) => {
    const drag = launcherDragRef.current;
    if (!drag.active) return;
    const delta = drag.startY - event.clientY;
    if (Math.abs(delta) > 4) drag.moved = true;
    setLauncherBottom(clampLauncherBottom(drag.startBottom + delta));
  };

  const handleLauncherPointerUp: React.PointerEventHandler<HTMLButtonElement> = (event) => {
    launcherDragRef.current.active = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const renderBubble = (m: ChatMsg) => {
    const base =
      "max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm whitespace-pre-wrap break-words leading-[1.6]";
    if (m.role === "user") {
      return (
        <div
          key={m.id}
          className={`ml-auto ${base}`}
          style={{ background: COLORS.teal, color: "#fff" }}
        >
          {m.text}
        </div>
      );
    }
    if (m.role === "agent") {
      return (
        <div
          key={m.id}
          className={`mr-auto ${base}`}
          style={{ background: COLORS.indigoSoft, color: COLORS.indigo }}
        >
          {m.text}
        </div>
      );
    }
    if (m.role === "bot") {
      return (
        <div
          key={m.id}
          className={`mr-auto ${base}`}
          style={{
            background: "#fff",
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
          }}
        >
          {m.text}
        </div>
      );
    }
    return (
      <div key={m.id} className="mx-auto text-[11px] text-[#6E6E6E]">
        {m.text}
      </div>
    );
  };

  const errorText =
    error != null ? t(`widget.errors.${error}` as const) : null;

  const statusText = connected
    ? t("widget.status.online")
    : error
    ? errorText
    : t("widget.status.connecting");

  if (onDashboard) return null;

  const widget = (
    <div
      className="fixed right-4 z-[9999] overflow-visible sm:right-6"
      style={{
        bottom: open ? 24 : launcherBottom,
        pointerEvents: "none",
      }}
      aria-live="polite"
    >
      <div className="relative">
        <div className="flex flex-col items-end gap-3">
          {/* Panel */}
          {open && (
            <div
              id="mc-chat-widget"
              className="pointer-events-auto flex h-[min(680px,calc(100vh-7rem))] w-[380px] max-w-[92vw] flex-col overflow-hidden rounded-[22px] border bg-white shadow-[0_32px_60px_rgba(15,15,35,0.12)]"
              style={{ borderColor: COLORS.border }}
              aria-live="polite"
            >
              {/* Header */}
              <div
                className="flex flex-col gap-1 px-5 py-4 text-white"
                style={{
                  background: `linear-gradient(90deg, ${COLORS.indigo} 0%, ${COLORS.teal} 100%)`,
                }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3
                      className="text-base font-semibold"
                      id="mc-chat-title"
                    >
                      {t("widget.title")}
                    </h3>
                    <p
                      className="text-sm text-white/80"
                      id="mc-chat-subtitle"
                    >
                      {statusText}
                    </p>
                  </div>
                  <div className="flex gap-3 text-white/90">
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        setUnread(0);
                      }}
                      className="text-lg hover:text-white"
                      aria-label={t("widget.actions.minimize")}
                    >
                      —
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        setUnread(0);
                      }}
                      className="text-lg hover:text-white"
                      aria-label={t("widget.actions.close")}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div
                ref={viewportRef}
                className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-white px-4 py-5"
              >
                {messages.length === 0 && (
                  <div className="text-sm text-[#6E6E6E]">
                    {t("widget.empty")}
                  </div>
                )}
                {messages.map(renderBubble)}
                {error && (
                  <div className="mx-auto text-[11px] text-[#D81F26]">
                    {errorText}
                  </div>
                )}
              </div>

              {/* Composer */}
              <form
                onSubmit={handleSubmit}
                className="flex flex-none items-end gap-3 border-t px-4 py-3"
                style={{
                  borderColor: COLORS.border,
                  background: COLORS.panelBgSoft,
                }}
              >
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  placeholder={t("widget.inputPlaceholder")}
                  className="min-h-[44px] max-h-24 min-w-0 flex-1 resize-none rounded-2xl border bg-white px-4 py-3 text-sm leading-relaxed text-[#414141] shadow-sm outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/20"
                  aria-label={t("widget.inputLabel")}
                  style={{ borderColor: COLORS.border }}
                />
                <button
                  type="submit"
                  className="grid h-11 w-11 flex-none place-items-center rounded-full bg-[#D81F26] text-white shadow-md transition hover:-translate-y-0.5 hover:bg-[#B71C1C] disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label={t("widget.actions.send")}
                  title={t("widget.actions.send")}
                  disabled={!connected || !input.trim()}
                >
                  <SendHorizontal size={18} strokeWidth={2.4} aria-hidden="true" />
                </button>
              </form>
            </div>
          )}

          {/* FAB */}
          {!open && (
            <button
              type="button"
              onClick={() => {
                if (launcherDragRef.current.moved) {
                  launcherDragRef.current.moved = false;
                  return;
                }
                setOpen(true);
                setUnread(0);
              }}
              onPointerDown={handleLauncherPointerDown}
              onPointerMove={handleLauncherPointerMove}
              onPointerUp={handleLauncherPointerUp}
              onPointerCancel={handleLauncherPointerUp}
              className="pointer-events-auto relative grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-[#2F2A7A] to-[#00B7B0] text-2xl text-white shadow-[0_20px_40px_rgba(15,15,35,0.12)] transition hover:-translate-y-1 hover:shadow-[0_28px_55px_rgba(15,15,35,0.18)]"
              aria-controls="mc-chat-widget"
              aria-expanded={open}
              aria-label={t("widget.actions.open")}
              title={t("widget.actions.open")}
            >
              <i className="fa-solid fa-message" aria-hidden="true" />
              {unread > 0 && (
                <span className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-[#D81F26] text-xs font-semibold text-white">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return widget;
  return createPortal(widget, document.body);
}
